import { useEffect, useState } from 'react';
import '../styles/CodeLineage.scss';
import SearchableDropdown from '../components/SearchableDropdown';
import Button from '../components/Button';
import advancedIcon from '../assets/png/ico_advanced.png';
import configureIcon from '../assets/png/ico_configure.png';
import downloadIcon from '../assets/png/ico_download.png';
import { getRepoGraphURL, getRepositoryDetails } from '../api/codeLineage.api';
import { useFetch } from '../hooks/useFetch';
import StatusModal from '../components/StatusModal';
import Loader from '../components/Loader';
import { useOverlaySidebar } from "../context/OverlaySidebarContext";
import { IoIosAddCircleOutline, IoIosRemoveCircleOutline } from "react-icons/io";
import { useNavigate } from 'react-router-dom';
import { LiaHandPointUp } from "react-icons/lia";
import InfoModal from '../components/InfoModal';
import { Tooltip } from 'primereact/tooltip';
import PageLoader from '../components/PageLoader';



function CodeLineage() {
  const navigate = useNavigate();

  const [repoList, setRepoList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [loader, setLoader] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);

  const [graphUrlString, setGraphUrlString] = useState(null);
  const [graphUrl, setGraphUrl] = useState(null);
  const [graphUrlRes, setGraphUrlRes] = useState(null);
  const [zoom, setZoom] = useState(1);

  const { toggleOverlay, setOnCloseCallback } = useOverlaySidebar();

  const [modal, setModal] = useState({
    open: false,
    type: "success",
    message: ""
  });
  const [open, setOpen] = useState(false);

  /**
   * Normalize API fields & decorate with UI metadata
   * API: Process_status -> process_status, Directory_path -> directory
   * Adds: _ui = { statusText, statusClass }
   */

  const decorateFiles = (files = []) =>
    files.map((f) => {
      const process_status =
        typeof f.Process_status === "number"
          ? f.Process_status
          : typeof f.process_status === "number"
            ? f.process_status
            : 2; // default: Normal

      const directory = f.Directory_path ?? f.directory ?? "";

      let statusText = "";
      let statusClass = "status-ok";

      if (process_status === 0) {
        statusText = "Not started";
        statusClass = "status-not-started";
      } else if (process_status === 1) {
        statusText = "Processing";
        statusClass = "status-processing";
      } else if (process_status === 2) {
        statusText = ""; // Normal -> no badge
        statusClass = "status-ok";
      } else if (process_status === 3) {
        statusText = "Failed";
        statusClass = "status-failed";
      }

      return {
        ...f,
        process_status,
        directory,
        _ui: { statusText, statusClass },
      };
    });

  const isFileDisabled = (opt) => Number(opt?.process_status) !== 2;

  // Fetch repositories
  const { data: repoResponse, loading, error } = useFetch(getRepositoryDetails, []);

  /**
   * Load previous state (repo/file/graph) from localStorage
   */
  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem("codeLineageData"));
    if (storedData) {
      setSelectedRepo(storedData.selectedRepo);
      setSelectedFile(storedData.selectedFile);
      setGraphUrlString(storedData.graphUrlString);
      setGraphUrlRes(storedData.graphUrlRes);
      const iframeURL = generateHTMLGraphUrl(storedData.graphUrlString);
      setGraphUrl(iframeURL);
    } else {
      setOpen(true);
      localStorage.removeItem("codeLineageData");
    }
  }, []);

  /**
   * Persist current selection and graph string
   */
  useEffect(() => {
    if (selectedRepo && graphUrlString) {
      const chatData = {
        selectedRepo,
        selectedFile,
        graphUrlString,
        graphUrlRes,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem("codeLineageData", JSON.stringify(chatData));
    }
  }, [selectedRepo, selectedFile, graphUrlString, graphUrlRes]);

  /**
   * Handle repository fetch response
   */
  useEffect(() => {
    if (repoResponse) {
      setRepoList(repoResponse.data);
    }
  }, [repoResponse]);

  /**
   * Error & loading states
   */
  useEffect(() => {
    if (error) {
      setModal({
        open: true,
        type: "error",
        message: "Failed to load repositories."
      });
    }
  }, [error]);

  useEffect(() => {
    setPageLoader(loading);
  }, [loading]);

  /**
   * When repoList or selectedRepo changes, compute fileList (decorated)
   */
  useEffect(() => {
    if (repoList && selectedRepo && selectedRepo.repo_id) {
      const filesFromRepo =
        repoList.find((r) => r.repo_id === selectedRepo.repo_id)?.files || [];
      setFileList(decorateFiles(filesFromRepo));
    }
  }, [repoList, selectedRepo]);

  /**
   * Refresh repos when overlay closes
   */
  setOnCloseCallback(() => {
    reloadApi();
  });

  const reloadApi = async () => {
    try {
      const response = await getRepositoryDetails();
      if (response.status === 200) {
        const resData = response.data?.data;
        setRepoList(resData);
      } else {
        setModal({
          open: true,
          type: "error",
          message: response.data?.data?.message || 'Failed to load repositories.'
        });
      }
    } catch (error) {
      setModal({
        open: true,
        type: "error",
        message: "Unexpected error while loading repositories."
      });
    }
  };

  /**
   * Handlers
   */
  const onRepoSelect = (repo) => {
    setSelectedRepo(repo);
    setSelectedFile(null);
    setGraphUrl(null);

    const files = Array.isArray(repo?.files) ? repo.files : [];
    setFileList(decorateFiles(files));
  };

  const onFileSelect = (file) => {
    setSelectedFile(file);
    setGraphUrl(null);
  };

  const handleRunActionClick = async () => {
    try {
      const repoId = selectedRepo?.repo_id || null;
      const fileId = selectedFile?.File_id || null;
      setLoader(true);

      const response = await getRepoGraphURL(repoId, fileId);
      if (response.status === 200) {
        const resData = response.data?.data;
        setGraphUrlString(resData.htmlStr);
        setGraphUrlRes(resData.htmlUrl);
        const iframeURL = generateHTMLGraphUrl(resData.htmlStr);
        setGraphUrl(iframeURL);
      } else {
        setModal({
          open: true,
          type: "error",
          message: response.data?.data?.message || 'Failed to load graph.'
        });
      }
    } catch (error) {
      setModal({
        open: true,
        type: "error",
        message: "Failed to load graph"
      });
    } finally {
      setLoader(false);
    }
  };

  /**
   * Graph helpers
   */
  const generateHTMLGraphUrl = (htmlString) => {
    if (!htmlString) return false;
    htmlString = htmlString.replace(
      /interaction\":\s*{[^}]*}/,
      `interaction": {
        dragNodes: true,
        dragView: true,
        zoomView: true,
        hideEdgesOnDrag: false,
        hideNodesOnDrag: false
      }`
    );
    const customCSS = `
      <style>
        #mynetwork {
          width: 100% !important;
          height: 95vh !important;
          background-color: transparent;
          border: none !important;
          position: relative;
          float: left;
        }
        .card {
          border: none !important;
        }
      </style>
    `;
    htmlString = htmlString.replace("</head>", `${customCSS}</head>`);
    const blob = new Blob([htmlString], { type: "text/html" });
    const iframeUrl = URL.createObjectURL(blob);
    return iframeUrl;
  };

  const generateHTMLGraphDownload = (htmlString) => {
    if (!htmlString) return false;
    htmlString = htmlString.replace(
      /interaction\":\s*{[^}]*}/,
      `interaction": {
        dragNodes: true,
        dragView: true,
        zoomView: true,
        hideEdgesOnDrag: false,
        hideNodesOnDrag: false
      }`
    );
    const customCSS = `
      <style>
        #mynetwork {
          width: 100% !important;
          height: 90vh !important;
          background-color: transparent;
          border: 1px solid #d8d4d4 !important;
          position: relative;
          float: left;
        }
        .card {
          border: none !important;
          padding: 0rem 1rem;
        }
      </style>
    `;
    const finalString = htmlString.replace("</head>", `${customCSS}</head>`);
    return finalString;
  };

  const downloadHtml = () => {
    const repoName = selectedRepo?.repo_name || '';
    const fileNameOriginal = selectedFile?.File_name || '';
    const fileName = selectedFile?.File_name.split('.').slice(0, -1).join('') || '';

    const fullHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <title>${repoName} Graph</title>
           <style>
                    .col {
                        display: flex;
                        align-items: center;
                        font-family: sans-serif;
                        font-size: 10px;
                        margin: 4px 0;
                    }
                    .col::before {
                        content: "";
                        display: inline-block;
                        width: 34px;
                        height: 10px;
                        margin-right: 8px;
                    }
                    .bronze::before {
                        background-color: #cd7f32; /* bronze */
                    }
                    .silver::before {
                        background-color: #c0c0c0; /* silver */
                    }
                    .gold::before {
                        background-color: #ffd700; /* gold */
                    }
         </style>
        </head>
        <body>
         <div style="margin:.5rem 1rem; display:flex">
             <div>
            <span style="color: #FD5108; font-weight: bold; font-family: sans-serif; font-size: 12px;">Repository :</span>
            <span style="color: #464646; font-family: sans-serif; font-size: 12px;">${repoName}</span>
            ${fileNameOriginal ? `
              <span style="color: #FD5108; font-weight: bold; margin-left: 20px; font-family: sans-serif; font-size: 12px;">File :</span>
              <span style="color: #464646; font-family: sans-serif; font-size: 12px;">${fileNameOriginal}</span>
            ` : ""}
              </div>
             <div style="display: flex;margin-left: 10%; width:350px" >
                <div class="col bronze">Bronze Layer</div>
                <div class="col silver">Silver Layer</div>
                <div class="col gold">Gold Layer</div>
            </div>
          </div>
          ${generateHTMLGraphDownload(graphUrlString)}
        </body>
      </html>
    `;

    const blob = new Blob([fullHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName ? `${repoName}_${fileName}.html` : `${repoName}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const clearChat = () => {
    setSelectedRepo(null);
    setSelectedFile(null);
    setGraphUrl(null);
    localStorage.removeItem('codeLineageData');
  };

  /**
   * Filter file types allowed in file dropdown
   */
  const allowedFileList = (fileList ?? []).filter(item => {
    const name = String(item?.File_name ?? '');
    return /\.(sql|py|cobol)$/i.test(name.trim());
  });

  return (
    <>
      <section className="clTopSection d-flex align-items-center">
        <div className="row w-100">
          <div className="col-8 col-xxl-9 d-flex align-items-center">
            {/* <label className='clText'> Lineage Studio</label>&nbsp;&nbsp; */}
            {/* Repository */}
            <div className='dropDownSection'>
              <label className='labelText'>Select repository</label>
              <SearchableDropdown
                options={repoList}
                label="repo_name"
                id="repo_id"
                selectedVal={selectedRepo}
                handleChange={(value) => onRepoSelect(value)}
                placeholder='Please select'
                showClear={false}
              />
            </div>

            {/* File */}
            <div className='dropDownSection'>
              <label className='labelText4'>Select file</label>
              <Tooltip target=".file-icon" appendTo={document.body} />

              {/* <SearchableDropdown
                options={allowedFileList}
                id="File_id"
                selectedVal={selectedFile}
                handleChange={onFileSelect}
                placeholder="Please select"
                showClear={true}
                isOptionDisabled={(opt) => Number(opt?.process_status) !== 2}
                getOptionLabel={(opt) => opt?.File_name ?? ''}

                renderOption={(opt) => (
                  <div className="file-option">
                    <span className="file-name">{opt?.File_name}</span>

                    {opt?._ui?.statusText && (
                      <span className={`status-badge ${opt?._ui?.statusClass}`}>
                        ({opt._ui.statusText})
                      </span>
                    )}

                    <span
                      className="file-icon"
                      role="img"
                      aria-label={opt?.directory ? `Directory: ${opt.directory}` : 'Directory not available'}
                      title={opt?.directory || 'No directory provided'}
                      data-pr-tooltip={String(opt?.directory || 'No directory provided')}
                      data-pr-position="right"
                    >
                      📁
                    </span>
                  </div>
                )}
              /> */}

              <SearchableDropdown
                options={allowedFileList}
                id="File_id"
                selectedVal={selectedFile}
                handleChange={onFileSelect}
                placeholder="Please select"
                showClear={true}
                isOptionDisabled={(opt) => Number(opt?.process_status) !== 2}
                getOptionLabel={(opt) => opt?.File_name ?? ''}

                renderOption={(opt) => {
                  const dir = opt?.directory || 'No directory provided';
                  return (
                    <div
                      className="file-option"
                      // Tooltip on hover over the entire row
                      title={dir}
                      aria-label={opt?.directory ? `Directory: ${opt.directory}` : 'Directory not available'}
                    >
                      <span className="file-name">{opt?.File_name}</span>

                      {opt?._ui?.statusText && (
                        <span className={`status-badge ${opt?._ui?.statusClass}`}>
                          ({opt._ui.statusText})
                        </span>
                      )}
                    </div>
                  );
                }}
              />


            </div>
            <div className='ps-lg-3'>
              <Button
                variant="primary"
                disabled={!(selectedRepo && selectedRepo.repo_id)}
                onClick={() => handleRunActionClick()}
              >
                SHOW
              </Button>
            </div>
          </div>

          <div className="col-4  col-xxl-3 d-flex align-items-center justify-content-between p-0">
            <span className='clOptionText' onClick={() => downloadHtml()}>
              <img src={downloadIcon} alt="download" className='clOptionIcon' />Download
            </span>
            <span className='clOptionText' onClick={() => navigate('/advanced-lineage-studio')}>
              <img src={advancedIcon} alt="advanced" className='clOptionIcon' />Advanced
            </span>
            <span className='clOptionText' onClick={toggleOverlay}>
              <img src={configureIcon} alt="configure" className='clOptionIcon' />Configure
            </span>
            <span className='clOptionText' onClick={() => setOpen(true)}>
              <i className='IconHelp' />
            </span>
          </div>

        </div>
      </section>

      <section className='graphSection'>
        {graphUrl && !loader && (
          <>
            <Tooltip target=".clearIcon" />
            <span
              className='clearIcon'
              onClick={() => clearChat()}
              data-pr-tooltip="Clear graph"
              data-pr-position="left"
            >
              <i className='ClearIcon3'></i>
            </span>
          </>
        )}

        {loader && (
          <div className='d-flex align-items-center justify-content-center'>
            <Loader show={loader} />
          </div>
        )}

        {!graphUrl && !loader && (
          <span className='showBlankText'>
            <LiaHandPointUp className='topHandIcon' />
            You have not selected a repository or file <br /> yet in the top pane.
          </span>
        )}

        {graphUrl && !loader && (
          <div className="graphImageBlock">
            <div className='colorInfoSection'>
              <div className="row">
                <div className="col bronze">Bronze Layer</div>
                <div className="col silver">Silver Layer</div>
                <div className="col gold">Gold Layer</div>
                <div className="col unknown">Unknown</div>
              </div>
            </div>
            <iframe
              key={graphUrl}
              src={graphUrl}
              title="Code Lineage Graph"
              style={{
                width: "100%",
                height: "96vh",
                border: "none",
                transform: `scale(${zoom})`,
                transformOrigin: "0 0",
              }}
            />
            <div className='zoomSection'>
              <IoIosAddCircleOutline
                className='zoomIcon'
                onClick={() => setZoom(prev => prev + 0.1)}
              />
              <br />
              <IoIosRemoveCircleOutline
                className='zoomIcon'
                onClick={() => setZoom(prev => Math.max(0.1, prev - 0.1))}
              />
            </div>
          </div>
        )}
      </section>

      <StatusModal
        open={modal.open}
        type={modal.type}
        message={modal.message}
        onClose={() => setModal({ ...modal, open: false })}
        autoClose={false}
        duration={5000}
      />

      <PageLoader show={pageLoader} />

      <InfoModal
        open={open}
        onClose={() => setOpen(false)}
        heading={
          <>
            Please select a repository and then a file from the top selection drop downs and then click on <strong>‘SHOW’</strong> button to view the detailed insight.
          </>
        }
        config='Configure lineage studio and add repository'
        advanced='Experience advanced lineage studio'
        download='Download detailed insights'
        title='Lineage studio help'
        type={1}
      />
    </>
  )
}

export default CodeLineage;